import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes, { any } from 'prop-types';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { getScheduledMatchesFromDayPlan } from '../../../utils/dayPlanner';
import { snapDownToNearestIncrement } from '../../../utils/courtSchedular';
import { TIME_FORMAT_12H } from '../../../utils/formatting';
import Match from './Match';

import styles from './dayPlanner.scss';

const DnDCalendar = withDragAndDrop(Calendar);

const DayPlannerCalendar = (props) => {
    const {
        className,
        event: eventData,
        'data-testid': dataTestId,
    } = props;

    const { eventSchedule } = eventData;

    const dayPlanner = useSelector(({ dayPlanner }) => dayPlanner);
    const {
        dayPlan,
    } = dayPlanner;

    const {
        dayConfig,
        viewState,
        enabledFacilitiesForDay,
        selectedScheduledVirtualDraw,
    } = dayPlanner;

    const localizer = useMemo(() => {
        if (eventSchedule) {
            moment.tz.setDefault(eventSchedule.timeZone?.value);
            return momentLocalizer(moment);
        }
    }, [eventSchedule]);

    useEffect(() => {
        return () => {
            moment.tz.setDefault();
        };
    }, []);

    const onScrollEvent = (contentIndex, list) => (scrollEvent) => {
        const scrollableItemsList = [
            ...list,
        ];
        if (contentIndex > -1) { // only splice array when item is found
            scrollableItemsList.splice(contentIndex, 1); // 2nd parameter means remove one item only
        }
        scrollableItemsList.forEach((item) => {
            item.scrollTop = scrollEvent.target.scrollTop;
        });
    };

    useEffect(() => {
        if (localizer) {
            const calendarContents = Array.from(document.getElementsByClassName('rbc-time-content'));
            if (calendarContents?.length > 0) {
                calendarContents.forEach((calendarContent, contentIndex, list) => {
                    calendarContent.addEventListener('scroll', onScrollEvent(contentIndex, list));
                });
            }

            return () => {
                if (calendarContents?.length > 0) {
                    calendarContents.forEach((calendarContent, contentIndex, list) => {
                        calendarContent.removeEventListener('scroll', onScrollEvent(contentIndex, list));
                    });
                }
            };
        }
    }, [localizer]);

    const resizableAccessor = useCallback(() => {
        return false;
    }, []);

    const draggableAccessor = useCallback(() => {
        return false;
    }, []);

    const minCalendarDate = useMemo(() => {
        return moment.utc(dayConfig.startTime).tz(eventSchedule?.timeZone?.value).toDate();
    }, [dayConfig, dayPlan, eventSchedule]);

    const maxCalendarDate = useMemo(() => {
        const midnight = moment.tz(dayConfig.day, eventSchedule?.timeZone?.value).endOf('day').toDate();
        if (dayPlan?.endLastMatch) {
            const justAfterLastMatch = moment.tz(dayPlan.endLastMatch, eventSchedule?.timeZone?.value).add('hours', 1).toDate();
            if (justAfterLastMatch < midnight) {
                return justAfterLastMatch;
            }
        }
        return midnight;
    }, [dayConfig, dayPlan, eventSchedule]);

    const dateOfConfig = useMemo(() => {
        return moment.tz(viewState.day, eventSchedule?.timeZone?.value).toDate();
    }, [viewState, eventSchedule]);

    const selectedFacility = useMemo(() => {
        return enabledFacilitiesForDay.find((facility) => {
            return facility.id === viewState.facilityId;
        });
    }, [dayConfig, enabledFacilitiesForDay, viewState]);

    const selectedCourtsOfSelectedFacility = useMemo(() => {
        if (selectedFacility) {
            return selectedFacility.courts.filter((court) => court.selected);
        }

        return [];
    }, [selectedFacility]);

    const timeFormats = useMemo(() => ({
        timeGutterFormat: (date) => moment(date).format(TIME_FORMAT_12H),
    }), []);

    const renderEventComponent = useCallback(({ event }) => {
        if (event?.data) {
            return (
                <Match
                    match={event?.data}
                    primaryView={viewState?.primaryView}
                    isScheduled
                    data-testid={dataTestId && `${dataTestId}.match-${event.data.id}`}
                />
            );
        }

        return null;
    }, [dataTestId]);

    const renderTodoWaitEventComponent = useCallback(({ event }) => {
        return event?.count;
    }, []);

    const isMatchNotInSelectedDraw = useCallback((match) => {
        return selectedScheduledVirtualDraw && selectedScheduledVirtualDraw !== match.drawId;
    }, [selectedScheduledVirtualDraw]);

    const calendarEvents = useMemo(() => {
        const matches = getScheduledMatchesFromDayPlan(dayPlan);
        return matches.map((match) => ({
            start: moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value).toDate(),
            end: moment.utc(match.endTime).tz(eventSchedule?.timeZone?.value).toDate(),
            resourceId: match.courtId,
            data: {
                ...match,
                addDisableOverlay: isMatchNotInSelectedDraw(match),
            },
        }));
    }, [dayPlan, isMatchNotInSelectedDraw]);

    const todoWaitListEvents = useMemo(() => {
        const waitingList = dayPlan?.dayProjection?.entrantsWaiting || [];
        const todoList = dayPlan?.dayProjection?.matchesPending || [];

        let eventsList = [];

        eventsList = eventsList.concat(waitingList.map((waitingObject) => {
            return {
                start: moment.utc(waitingObject.intervalStartTime).tz(eventSchedule?.timeZone?.value).toDate(),
                end: moment.utc(waitingObject.intervalStartTime).tz(eventSchedule?.timeZone?.value).add(snapDownToNearestIncrement(dayConfig.incrementMinutes), 'minutes').toDate(),
                resourceId: 1,
                count: waitingObject.count,
            };
        }));

        eventsList = eventsList.concat(todoList.map((waitingObject) => {
            return {
                start: moment.utc(waitingObject.intervalStartTime).tz(eventSchedule?.timeZone?.value).toDate(),
                end: moment.utc(waitingObject.intervalStartTime).tz(eventSchedule?.timeZone?.value).add(snapDownToNearestIncrement(dayConfig.incrementMinutes), 'minutes').toDate(),
                resourceId: 2,
                count: waitingObject.count,
            };
        }));

        return eventsList;
    }, [dayPlan, dayConfig, eventSchedule]);

    if (!localizer) {
        return null;
    }

    return (
        <div className={`${styles.dayPlannerCalendar} ${className}`} data-testid={dataTestId}>
            <div className={`${styles.wrapper}`}>
                <div className={`d-flex`} style={{ height: '100%' }}>
                    <DnDCalendar
                        className={styles.calendar}
                        defaultDate={dateOfConfig}
                        defaultView={Views.DAY}
                        events={calendarEvents}
                        localizer={localizer}
                        resourceIdAccessor="id"
                        resources={selectedCourtsOfSelectedFacility}
                        resourceTitleAccessor="name"
                        dayLayoutAlgorithm="no-overlap"
                        step={snapDownToNearestIncrement(dayConfig.incrementMinutes)}
                        timeslots={1}
                        views={[Views.DAY]}
                        toolbar={false}
                        min={minCalendarDate}
                        max={maxCalendarDate}
                        draggableAccessor={draggableAccessor}
                        resizableAccessor={resizableAccessor}
                        components={{
                            event: renderEventComponent,
                        }}
                        formats={timeFormats}
                    />
                    <Calendar
                        className={styles.rightIndexCalendar}
                        defaultDate={dateOfConfig}
                        defaultView={Views.DAY}
                        events={todoWaitListEvents}
                        localizer={localizer}
                        resourceIdAccessor="id"
                        resources={[{
                            id: 1,
                            name: 'Wait',
                        },
                        {
                            id: 2,
                            name: 'Todo',
                        }]}
                        resourceTitleAccessor="name"
                        dayLayoutAlgorithm="no-overlap"
                        step={snapDownToNearestIncrement(dayConfig.incrementMinutes)}
                        timeslots={1}
                        views={[Views.DAY]}
                        toolbar={false}
                        min={minCalendarDate}
                        max={maxCalendarDate}
                        components={{
                            event: renderTodoWaitEventComponent,
                        }}
                        formats={timeFormats}
                    />
                </div>
            </div>
        </div>
    );
};

DayPlannerCalendar.propTypes = {
    className: PropTypes.string,
    event: PropTypes.objectOf(any).isRequired,
    'data-testid': PropTypes.string,
};

DayPlannerCalendar.defaultProps = {
    className: '',
    'data-testid': undefined,
};

export default DayPlannerCalendar;
